import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isValidPositiveNumber,
  queryIsLoading,
  useGetFootprintingDairyPrimaryDataById,
  useGetFootprintingDairyRefrigerantUsage,
  useGetFootprintingEmissionFactorsRefrigerants,
  useKeyedFormList,
  useMutateFootprintingDairyPrimaryData,
  useMutateFootprintingDairyRefrigerantUsage,
  useTranslation,
} from "utils";

import {
  CFP_REFRIGERANT_RECOVERY_TYPE,
  CFP_REFRIGERANT_SUBAPPLICATION,
  DAIRY_REFRIGERANTS_ACTIVE,
  ENUM_DAIRY_REFRIGERANTS_ACTIVE,
  UploadableRefrigerant,
} from "models";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumber,
  KlimRadioGroup,
  Option,
  PageSpin,
  Select,
  Tooltip,
  Typography,
} from "components";

export interface DairyRefrigerantsFormProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const refrigerantPlaceholder: UploadableRefrigerant = {
  id: undefined,
  emissionFactorRefrigerantId: "",
  amountKg: null,
};

const DairyRefrigerantsForm = ({ primaryDataId, readOnly, farmId, year }: DairyRefrigerantsFormProps) => {
  const { t } = useTranslation("footprintingDairy");
  const formRef = useRef<FormInstance>(null);
  const selectGroupName = "footprinting-dairy-refrigerants";
  const [refrigerantsActive, setRefrigerantsActive] = useState<ENUM_DAIRY_REFRIGERANTS_ACTIVE | null>(null);
  const isRefrigerantsActive = refrigerantsActive === DAIRY_REFRIGERANTS_ACTIVE.YES;

  const dairyPrimaryDataQuery = useGetFootprintingDairyPrimaryDataById(primaryDataId ?? "");

  const refrigerantsQuery = useGetFootprintingEmissionFactorsRefrigerants();
  const dairyRefrigerantUsageQuery = useGetFootprintingDairyRefrigerantUsage(primaryDataId);
  const isLoading = [dairyPrimaryDataQuery, dairyRefrigerantUsageQuery, refrigerantsQuery].some(queryIsLoading);
  const keyedRefrigerants = useKeyedFormList<UploadableRefrigerant>(formRef, { isReady: !isLoading });

  const { mutate: mutateDairyRefrigerantUsage } = useMutateFootprintingDairyRefrigerantUsage(
    farmId,
    year,
    keyedRefrigerants.payload.filter((r) => !!r.emissionFactorRefrigerantId),
    primaryDataId
  );

  const { mutate: mutateDairyPrimaryData } = useMutateFootprintingDairyPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      refrigerantsActive,
    },
    primaryDataId
  );

  useEffect(
    () => () => {
      if (!readOnly && !isLoading && dairyPrimaryDataQuery.data?.id) {
        mutateDairyPrimaryData({});
        mutateDairyRefrigerantUsage();
      }
    },
    [dairyPrimaryDataQuery.data?.id, isLoading, mutateDairyPrimaryData, mutateDairyRefrigerantUsage, readOnly]
  );

  // Note - this will only run once because the query above is only the GET
  useEffect(() => {
    if (dairyRefrigerantUsageQuery.data && !!dairyRefrigerantUsageQuery.data.length) {
      keyedRefrigerants.actions.hydrate(
        dairyRefrigerantUsageQuery.data.map(
          ({ id, emissionFactorRefrigerantId, amountKg, subapplication, unitCount, unitAge, equipmentEol, refrigerantRecoveryType }) => ({
            id,
            emissionFactorRefrigerantId,
            amountKg,
            subapplication,
            unitCount,
            unitAge,
            equipmentEol,
            refrigerantRecoveryType,
          })
        )
      );
    } else if (!queryIsLoading(dairyRefrigerantUsageQuery)) {
      // If we're not loading but the user has refrigerants active,
      // set the keyedRefrigerants.entries to the placeholder
      keyedRefrigerants.actions.hydrate([refrigerantPlaceholder]);
    }
  }, [dairyRefrigerantUsageQuery.data, keyedRefrigerants.actions]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dairyPrimaryDataQuery.data && dairyPrimaryDataQuery.isSuccess) {
      setRefrigerantsActive(dairyPrimaryDataQuery.data.refrigerantsActive ?? null);
      const currentFormValues = formRef.current?.getFieldsValue() || {};

      formRef.current?.setFieldsValue({
        refrigerantsActive: dairyPrimaryDataQuery.data.refrigerantsActive ?? null,
        ...currentFormValues,
      });

      // this stops the inputs from validating on first form load, but will validate if user
      // nagivates forward and then back with an input that was left as null
      const fieldsToValidate = dairyPrimaryDataQuery.data.refrigerantsActive != null;

      if (fieldsToValidate) {
        keyedRefrigerants.actions.requestRehydrationValidation();
      }
    }
  }, [dairyPrimaryDataQuery.data, dairyPrimaryDataQuery.isSuccess, keyedRefrigerants.actions]);

  if (primaryDataId && isLoading) return <PageSpin />;

  const disabledRefrigerants = keyedRefrigerants.entries.reduce((acc, r) => ({ ...acc, [r.emissionFactorRefrigerantId]: true }), {} as Record<string, boolean>);

  return (
    <Card border dataTestId="footprinting-dairy-refrigerants-form" className="padding-200">
      <Form ref={formRef} preserve={false}>
        <Flex vertical gap={16}>
          <Flex align="center" gap={16} justify="space-between">
            <Typography variant="paragraph-m" style={{ maxWidth: "60%" }}>
              {t("dairyRefrigerantsForm.question.refrigerantsUsed")}
              <span className="required-asterisk">*</span>
              <Tooltip title={t("dairyRefrigerantsForm.tooltips.refrigerantsUsed")}>
                <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
              </Tooltip>
            </Typography>
            <KlimRadioGroup
              name="refrigerantsActive"
              dataTestId="footprinting-dairy-refrigerants-active-radio"
              required
              vertical={false}
              disabled={readOnly}
              type="tertiary"
              size="large"
              onChange={({ target }) => {
                setRefrigerantsActive(target.value);
              }}
              value={refrigerantsActive}
              defaultValue={refrigerantsActive}
              optionType="button"
              options={Object.values(DAIRY_REFRIGERANTS_ACTIVE).map((opt) => ({
                label: t(`footprinting:radioAnswer.${opt === DAIRY_REFRIGERANTS_ACTIVE.YES}`),
                value: opt,
              }))}
            />
          </Flex>
          {isRefrigerantsActive && (
            <Flex vertical gap={16}>
              {keyedRefrigerants.entries.map((refrigerant, index) => {
                const selectGroupWithIndex = `${selectGroupName}-${index}`;
                const selectGroupWithKey = `${selectGroupName}-${refrigerant.clientKey}`;
                return (
                  <div key={refrigerant.clientKey}>
                    <Flex vertical gap={16}>
                      <Flex justify="space-between" vertical={false}>
                        <Typography variant="h3" style={{ flex: 1 }}>
                          {t("dairyRefrigerantsForm.question.title") + (index + 1)}
                        </Typography>
                        <IconButton
                          type="tertiary"
                          disabled={readOnly}
                          dataTestId={`${selectGroupWithIndex}-delete`}
                          size="small"
                          iconName="tabler/trash"
                          onClick={() => {
                            keyedRefrigerants.actions.remove(refrigerant.clientKey);
                            if (keyedRefrigerants.entries.length === 1) setRefrigerantsActive(null);
                          }}
                        />
                      </Flex>

                      <Select
                        name={selectGroupWithKey}
                        disabled={readOnly}
                        dataTestId={selectGroupWithIndex}
                        label={t("dairyRefrigerantsForm.question.refrigerantType")}
                        tooltip={t("dairyRefrigerantsForm.tooltips.refrigerantType")}
                        rules={[{ required: true, message: "" }]}
                        initialValue={refrigerant.emissionFactorRefrigerantId}
                        value={refrigerant.emissionFactorRefrigerantId}
                        onChange={(v) => keyedRefrigerants.actions.update(refrigerant.clientKey, { emissionFactorRefrigerantId: v })}
                        dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                      >
                        {(refrigerantsQuery.data ?? []).map((refrigerant) => (
                          <Option
                            disabled={readOnly || disabledRefrigerants[refrigerant.id]}
                            key={`${selectGroupWithIndex}-${refrigerant.id}`}
                            data-testid={`${selectGroupWithIndex}-${refrigerant.id}`}
                            value={refrigerant.id}
                            label={refrigerant.name}
                          >
                            {refrigerant.name}
                          </Option>
                        ))}
                      </Select>

                      <InputNumber
                        name={`${selectGroupWithKey}-amount`}
                        dataTestId={`${selectGroupWithIndex}-amount`}
                        label={t("dairyRefrigerantsForm.question.refrigerantAmount")}
                        tooltip={t("dairyRefrigerantsForm.tooltips.refrigerantAmount")}
                        suffix="kg"
                        disabled={readOnly}
                        value={refrigerant.amountKg}
                        initialValue={refrigerant.amountKg}
                        onChange={(v) => keyedRefrigerants.actions.update(refrigerant.clientKey, { amountKg: v })}
                        rules={[
                          { required: true, message: t("dairyRefrigerantsForm.question.refrigerantQuestionError") },
                          { type: "number" },
                          {
                            validator: (_: unknown, v: number) => (isNullOrUndefined(v) || isValidNonnegativeNumber(v) ? Promise.resolve() : Promise.reject()),
                            message: t("dairyRefrigerantsForm.question.refrigerantQuestionError"),
                          },
                        ]}
                      />

                      <Select
                        name={`${selectGroupWithKey}-subapplication`}
                        disabled={readOnly}
                        dataTestId={`${selectGroupWithIndex}-subapplication`}
                        label={t("dairyRefrigerantsForm.question.subapplication")}
                        tooltip={t("dairyRefrigerantsForm.tooltips.subapplication")}
                        value={refrigerant.subapplication}
                        initialValue={refrigerant.subapplication}
                        rules={[{ required: true, message: "" }]}
                        onChange={(v) => keyedRefrigerants.actions.update(refrigerant.clientKey, { subapplication: v ?? null })}
                        dropdownStyle={{ zIndex: 10010 }}
                      >
                        {Object.values(CFP_REFRIGERANT_SUBAPPLICATION).map((value) => (
                          <Option key={`${selectGroupWithIndex}-subapplication-${value}`} value={value} label={value}>
                            {value}
                          </Option>
                        ))}
                      </Select>

                      <InputNumber
                        name={`${selectGroupWithKey}-unitCount`}
                        dataTestId={`${selectGroupWithIndex}-unitCount`}
                        label={t("dairyRefrigerantsForm.question.unitCount")}
                        tooltip={t("dairyRefrigerantsForm.tooltips.unitCount")}
                        disabled={readOnly}
                        value={refrigerant.unitCount}
                        initialValue={refrigerant.unitCount}
                        onChange={(v) => keyedRefrigerants.actions.update(refrigerant.clientKey, { unitCount: v })}
                        rules={[
                          { required: true, message: t("dairyRefrigerantsForm.question.unitCountError") },
                          { type: "number" },
                          {
                            validator: (_: unknown, v: number) => (isNullOrUndefined(v) || isValidPositiveNumber(v) ? Promise.resolve() : Promise.reject()),
                            message: t("dairyRefrigerantsForm.question.unitCountError"),
                          },
                        ]}
                      />

                      <InputNumber
                        name={`${selectGroupWithKey}-unitAge`}
                        dataTestId={`${selectGroupWithIndex}-unitAge`}
                        label={t("dairyRefrigerantsForm.question.unitAge")}
                        tooltip={t("dairyRefrigerantsForm.tooltips.unitAge")}
                        suffix="years"
                        disabled={readOnly}
                        value={refrigerant.unitAge}
                        initialValue={refrigerant.unitAge}
                        onChange={(v) => keyedRefrigerants.actions.update(refrigerant.clientKey, { unitAge: v })}
                        rules={[
                          { required: true, message: t("dairyRefrigerantsForm.question.unitAgeError") },
                          { type: "number" },
                          {
                            validator: (_: unknown, v: number) =>
                              v === null || v === undefined || (Number.isInteger(v) && v >= 0) ? Promise.resolve() : Promise.reject(),
                            message: t("dairyRefrigerantsForm.question.unitAgeError"),
                          },
                        ]}
                      />

                      <Select
                        name={`${selectGroupWithKey}-equipmentEol`}
                        disabled={readOnly}
                        dataTestId={`${selectGroupWithIndex}-equipmentEol`}
                        label={t("dairyRefrigerantsForm.question.equipmentEol")}
                        tooltip={t("dairyRefrigerantsForm.tooltips.equipmentEol")}
                        value={refrigerant.equipmentEol}
                        initialValue={refrigerant.equipmentEol}
                        rules={[{ required: true, message: "" }]}
                        onChange={(v) =>
                          keyedRefrigerants.actions.update(refrigerant.clientKey, {
                            equipmentEol: v ?? null,
                            refrigerantRecoveryType: v ? refrigerant.refrigerantRecoveryType : null,
                          })
                        }
                        dropdownStyle={{ zIndex: 10010 }}
                      >
                        {[true, false].map((opt) => (
                          <Option key={`${selectGroupWithIndex}-equipmentEol-${opt}`} value={opt} label={t(`footprinting:radioAnswer.${opt}`)}>
                            {t(`footprinting:radioAnswer.${opt}`)}
                          </Option>
                        ))}
                      </Select>

                      {refrigerant.equipmentEol ? (
                        <Select
                          name={`${selectGroupWithKey}-refrigerantRecoveryType`}
                          disabled={readOnly}
                          dataTestId={`${selectGroupWithIndex}-refrigerantRecoveryType`}
                          label={t("dairyRefrigerantsForm.question.refrigerantRecoveryType")}
                          tooltip={t("dairyRefrigerantsForm.tooltips.refrigerantRecoveryType")}
                          value={refrigerant.refrigerantRecoveryType}
                          initialValue={refrigerant.refrigerantRecoveryType}
                          rules={[{ required: true, message: "" }]}
                          onChange={(v) => keyedRefrigerants.actions.update(refrigerant.clientKey, { refrigerantRecoveryType: v ?? null })}
                          dropdownStyle={{ zIndex: 10010 }}
                        >
                          {Object.values(CFP_REFRIGERANT_RECOVERY_TYPE).map((value) => (
                            <Option key={`${selectGroupWithIndex}-refrigerantRecoveryType-${value}`} value={value} label={value}>
                              {value}
                            </Option>
                          ))}
                        </Select>
                      ) : null}
                    </Flex>
                  </div>
                );
              })}
            </Flex>
          )}
          {isRefrigerantsActive && (
            <Button
              type="text"
              disabled={readOnly}
              className="padding-left-25"
              onClick={() => {
                keyedRefrigerants.actions.add(refrigerantPlaceholder);
              }}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("dairyRefrigerantsForm.add")}
            </Button>
          )}
        </Flex>
      </Form>
    </Card>
  );
};

DairyRefrigerantsForm.Key = "dairy.refrigerants";
export default DairyRefrigerantsForm;
